import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOrg } from '../org/OrgContext';
import { useAuth } from '../auth/AuthContext';
import { fmtInt, fmtGBP, fmtDay } from '../utils/format';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';

function StatCard({ label, value, hint, to }) {
  const display =
    typeof value === 'number' && Number.isFinite(value)
      ? fmtInt(value)
      : (value ?? '—');

  const inner = (
    <>
      {label && <div className="stat-label">{label}</div>}
      <div className="stat-value">{display}</div>
      {hint && <div className="stat-hint">{hint}</div>}
      {to && <span className="stat-arrow"><Icon name="arrowRight" size={16} /></span>}
    </>
  );

  return to ? (
    <Link to={to} className="stat-link">
      <div className="card stat-card clickable">{inner}</div>
    </Link>
  ) : (
    <div className="card stat-card">{inner}</div>
  );
}

// Activity rows carry "YYYY-MM-DD", ISO timestamps or fleet-style "14/10/2026 08:12".
function activityTime(h = {}) {
  const v = String(h.date || h.ts || '');
  const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(v);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0)).getTime();
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : 0;
}

function eventClass(evt = '') {
  const e = String(evt).toLowerCase();
  if (e.startsWith('delivery')) return 'badge evt delivery';
  if (e.startsWith('withdrawal')) return 'badge evt withdrawal';
  return 'badge';
}

function ActivityRow({ item, org }) {
  const amount =
    item.amount ?? item.delta ?? item.qty ?? null;
  const unitP =
    item.unitPrice != null ? fmtGBP(item.unitPrice) : '—';
  const totalP = (() => {
    if (item.totalPrice != null) return fmtGBP(item.totalPrice);
    if (item.unitPrice != null && item.amount != null) {
      return fmtGBP(Number(item.unitPrice) * Number(item.amount));
    }
    return '—';
  })();

  return (
  <tr>
    <td className="nowrap">{fmtDay(item.date || item.ts)}</td>
    <td><span className={eventClass(item.event || item.action)}>{item.event || item.action || '—'}</span></td>
    <td className="cell-muted">{item.team || item.site || item.numberPlate || '—'}</td>
    <td>{item.item || item.summary?.item || item.location || '—'}</td>
    <td className="num">{amount == null ? '—' : fmtInt(amount)}</td>
    {org !== 'GPM' && (
      <>
        <td className="num">{unitP}</td>
        <td className="num">{totalP}</td>
      </>
    )}
  </tr>

  );
}

function PendingDeliveriesTable({ rows = [] }) {
  if (!rows.length) {
    return <p style={{ color: 'var(--muted)' }}>No pending deliveries.</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Amount</th>
            <th>Unit Cost</th>
            <th>Supplier</th>
            <th>Office</th>
            <th>Ordered By</th>
            <th>Ordered</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(d => (
            <tr key={d._id}>
              <td style={{ fontWeight: 600 }}>{d.item}</td>
              <td>{fmtInt(d.amount || 0)}</td>
              <td>£{Number(d.unitCost || 0).toFixed(2)}</td>
              <td>{d.supplier || '—'}</td>
              <td>{d.office || 'main'}</td>
              <td>{d.orderedBy || '—'}</td>
              <td>{d.orderedDate ? new Date(d.orderedDate).toLocaleDateString('en-GB') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { org } = useOrg();             // 'GFM' | 'GPM' | 'Fleet' | 'Assets'
  const { user, api } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [history, setHistory] = useState([]);    // normalized for Recent Activity
  const [history2, setHistory2] = useState([]);  // raw/secondary for Fleet cars
  const [pendingDeliveries, setPendingDeliveries] = useState([]);

  // counts shown in tiles; fill what applies for the current org
  const [stats, setStats] = useState({
    // GFM
    invMain: 0, invguildford: 0, invNorthern: 0,
    // GPM
    gpmInv: 0, pendingDeliveries: 0,
    // Fleet
    vehicles: 0, bookings: 0, checks: 0,
    vans: 0, cars: 0,
    // Assets (top-level)
    assetsGlobal: 0,
  });

  // Newest first regardless of the order the API returns (GFM history comes
  // newest-first, GPM/assets oldest-first). Ties keep the later entry first.
  const recent = useMemo(() =>
    (history || [])
      .map((h, i) => ({ h, i, t: activityTime(h) }))
      .sort((a, b) => (b.t - a.t) || (b.i - a.i))
      .slice(0, 12)
      .map(x => x.h),
  [history]);

  const last30Days = useMemo(() => {
    const since = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return (history || []).filter(h => activityTime(h) >= since).length;
  }, [history]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      setHistory([]); setHistory2([]);

      try {
        if (org === 'GFM') {
          const [main, gui, nth, hist, deliveries] = await Promise.all([
            api.get('/gfm/inventory/main').then(r => r.data).catch(() => []),
            api.get('/gfm/inventory/guildford').then(r => r.data).catch(() => []),
            api.get('/gfm/inventory/northern').then(r => r.data).catch(() => []),
            api.get('/gfm/history').then(r => r.data).catch(() => []),
            api.get('/gfm/deliveries').then(r => r.data).catch(() => []),
          ]);
          if (!alive) return;
          setHistory(Array.isArray(hist) ? hist : []);
          const pending = (Array.isArray(deliveries) ? deliveries : []).filter(d => !d.deliveredDate);
          setPendingDeliveries(pending);
          setStats(s => ({ ...s,
            invMain: main.length, invguildford: gui.length, invNorthern: nth.length, pendingDeliveries: pending.length,
          }));
        }

        if (org === 'GPM') {
          const [inv, deliveries, hist] = await Promise.all([
            api.get('/gpm/inventory/main').then(r => r.data).catch(() => []),
            api.get('/gpm/deliveries').then(r => r.data).catch(() => []),
            api.get('/gpm/history').then(r => r.data).catch(() => []),
          ]);
          if (!alive) return;
          const pending = (Array.isArray(deliveries) ? deliveries : []).filter(d => !d.deliveredDate);
          setPendingDeliveries(pending);
          setStats(s => ({ ...s,
            gpmInv: Array.isArray(inv) ? inv.length : 0,
            pendingDeliveries: pending.length,
          }));
          setHistory(Array.isArray(hist) ? hist : []);
        }

        if (org === 'Fleet') {
          const [vehicles, historyWrap, bookings, checks] = await Promise.all([
            api.get('/fleet/vehicles').then(r => r.data).catch(() => []),
            api.get('/fleet/history').then(r => r.data).catch(() => ({ vehicles: [] })),
            api.get('/fleet/checks').then(r => r.data).catch(() => []),
          ]);
          if (!alive) return;
          setStats(s => ({
            ...s,
            vehicles: vehicles.length,
            bookings: Array.isArray(bookings) ? bookings.length : 0,
            checks:   Array.isArray(checks) ? checks.length : 0,
          }));

          // Flatten unified history -> Recent Activity
          const flat = [];
          const list = Array.isArray(historyWrap?.vehicles) ? historyWrap.vehicles : [];
          for (const v of list) {
            const reg = v.reg;
            const histArr = Array.isArray(v.history) ? v.history : [];
            for (const h of histArr) {
              flat.push({
                date: h.at,                 // already display-formatted string
                event: 'vehicle.status',
                numberPlate: reg,
                location: h.location,
                status: h.status,
              });
            }
          }
          flat.sort((a,b) => (String(a.date) < String(b.date) ? 1 : -1));
          setHistory(flat);
          setHistory2([]); // no secondary list in ified flow
        }

        if (org === 'Assets') {
          const [assets, assetHist, tickets] = await Promise.all([
            api.get('/assets').then(r => r.data).catch(() => []),
            api.get('/assets/history').then(r => r.data).catch(() => []),
            api.get('/assets/tickets').then(r => r.data).catch(() => []), // if tickets are relevant later
          ]);
          if (!alive) return;
          setStats(s => ({ ...s,
            assetsGlobal: assets.length,
            tickets: tickets.length,
          }));
          setHistory(Array.isArray(assetHist) ? assetHist : []);
        }
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load dashboard');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [org, api]);

  async function testPing() {
    try {
      setError('');
      if (org === 'GFM')      await api.get('/gfm/history');
      else if (org === 'GPM') await api.get('/gpm/history');
      else if (org === 'Fleet') await api.get('/fleet/vehicles');
      else if (org === 'Assets') await api.get('/assets');
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Ping failed');
    }
  }

  async function forceRefresh() {
    try {
      setError('');
      const token = localStorage.getItem('refreshToken');
      const base  = api?.defaults?.baseURL;
      if (!token || !base) throw new Error('Missing refresh token or baseURL');
      const res = await fetch(`${base}/auth/refresh`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ refreshToken: token }) });
      if (!res.ok) throw new Error('Refresh failed');
      const j = await res.json();
      if (j?.accessToken) localStorage.setItem('accessToken', j.accessToken);
    } catch (e) {
      setError(e?.message || 'Refresh failed');
    }
  }

  // inside Dashboard component, after your state hooks:
const [assetIndex, setAssetIndex] = useState({}); // { [id]: "Name (Model)" }

useEffect(() => {
  let alive = true;
  (async () => {
    if (org !== 'Assets') return;

    try {
      // If you already fetch assets elsewhere, reuse that list instead.
      const assets = await api.get('/assets').then(r => r.data).catch(() => []);
      if (!alive) return;

      const idx = {};
      for (const a of Array.isArray(assets) ? assets : []) {
        const name = String(a.item || a.name || 'Unknown').trim();
        const model = String(a.model || '').trim();
        idx[a.id] = model ? `${name} (${model})` : name;
      }
      setAssetIndex(idx);
    } catch {
      /* ignore, we'll just show the id */
    }
  })();
  return () => { alive = false; };
}, [org, api]);

function ActivityRowAssets({ item, assetIndex }) {
  const label = assetIndex?.[item.id] || item.id || '—';
  return (
    <tr>
      <td className="nowrap">{fmtDay(item.date || item.ts)}</td>
      <td className="cell-strong">{label}</td>
      <td className="cell-muted">{item.startPosition || '—'}</td>
      <td>{item.endPosition || '—'}</td>
    </tr>
  );
}

function ActivityRowFleet({ item }) {
  // item shape from your flattened history: { date, numberPlate, status, location }
  return (
    <tr>
      <td className="nowrap">{item.date || '—'}</td>
      <td className="cell-strong">{item.numberPlate || '—'}</td>
      <td><span className="badge">{item.status || '—'}</span></td>
      <td>{item.location || '—'}</td>
    </tr>
  );
}



  return (
    <div className="container">
      <PageHeader
        eyebrow={org}
        title="Dashboard"
        sub={<>Signed in as <strong>{user?.username}</strong></>}
        actions={
          <>
            <button className="btn secondary small" onClick={() => nav('/choose')}>Change division</button>
            <button className="btn secondary small" onClick={testPing}>Refresh</button>
            <button className="btn secondary small" onClick={forceRefresh}>Extend session</button>
          </>
        }
      />

      {error && <div className="page-alerts"><div className="alert error">{error}</div></div>}

      {/* GFM tiles */}
      {org === 'GFM' && (
        <>
          <div className="grid grid-4 mb-16">
            <StatCard label="Inventory" value="Main" to="/inventory/main" />
            <StatCard label="Inventory" value="Guildford" to="/inventory/guildford" />
            <StatCard label="Inventory" value="Leeds" to="/inventory/northern" />
            <StatCard label="Inventory" value="Vizion 7" to="/inventory/vision7" />
          </div>
          <div className="grid grid-3 mb-16">
            <StatCard label="Activity (30 days)" value={last30Days} to="/history" />
            <StatCard label="Orders and deliveries" value={stats.pendingDeliveries + " pending"} to={"/gfm/deliveries"} />
            <StatCard label="Teams" value="Edit teams" to="/teams" />
          </div>
        </>
      )}

      {org === 'GPM' && (
        <>
          <div className="grid grid-3 mb-16">
            <StatCard label="Inventory" value={stats.gpmInv} hint="items" to="/gpm/inventory" />
            <StatCard label="Orders and deliveries" value={stats.pendingDeliveries + " pending"} to="/gpm/deliveries" />
            <StatCard label="History" value={history.length} hint="entries" to="/gpm/history" />
          </div>
{/*
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h3 style={{ margin: 0 }}>Pending Deliveries</h3>
              <Link to="/gpm/deliveries"><button className="btn small">View all</button></Link>
            </div>
            <div style={{ marginTop: 12 }}>
              <PendingDeliveriesTable rows={pendingDeliveries.slice(0, 8)} />
            </div>
          </div> */}
        </>
      )}

      {/* Fleet tiles */}
      {org === 'Fleet' && (
        <div className="grid grid-3 mb-16">
          <StatCard label="Vehicles" value={stats.vehicles} to="/vehicles" />
          <StatCard label="Vehicle check" value="New check" to="/fleetChecks" />
          <StatCard label="Review checks" value={stats.bookings} to="/fleetChecksList" />
        </div>
      )}

      {/* Assets tiles */}
      {org === 'Assets' && (
        <div className="grid grid-3 mb-16">
          <StatCard label="Assets" value={stats.assetsGlobal} to="/assets" />
          <StatCard label="Asset moves" value={history.length} />
          <StatCard label="Tickets" value={stats.tickets} to="/tickets" />
        </div>
      )}

      {/* Recent Activity */}
      <div className="card">
        <div className="card-head">
          <h2 className="card-title">Recent activity</h2>
          {loading && <span className="badge">Loading</span>}
        </div>

        {(!loading && recent.length === 0) ? (
          <div className="empty">No history yet.</div>
        ) : (
          <div className="table-wrap">
  <table className="table">
    <thead>
      {org === 'Assets' ? (
        <tr>
          <th>Date</th>
          <th>Asset</th>
          <th>From</th>
          <th>To</th>
        </tr>
      ) : org === 'GPM' ? (
        <tr>
          <th>Date</th>
          <th>Event</th>
          <th>Team</th>
          <th>Item</th>
          <th className="num">Qty</th>
        </tr>
      ) : org === 'Fleet' ? (
    <tr>
      <th>Date</th>
      <th>Plate</th>
      <th>Status</th>
      <th>Location</th>
    </tr>
  ) : (
        <tr>
          <th>Date</th>
          <th>Event</th>
          <th>Team</th>
          <th>Item</th>
          <th className="num">Qty</th>
          <th className="num">Unit price</th>
          <th className="num">Total</th>
        </tr>
      )}
    </thead>

<tbody>
  {recent.map((h, i) =>
    org === 'Assets' ? (
      <ActivityRowAssets
        key={h.id || `${h.date}-${i}`}
        item={h}
        assetIndex={assetIndex}
      />
    ) : org === 'Fleet' ? (
      <ActivityRowFleet
        key={`${h.numberPlate || 'x'}-${h.date || i}-${i}`}
        item={h}
      />
    ) : (
      <ActivityRow
        key={h.id || `${h.event || h.action || 'x'}-${h.date || h.ts}-${i}`}
        item={h}
        org={org}
      />
    )
  )}
</tbody>

  </table>
</div>

        )}
      </div>
    </div>
  );
}
